import React, { Fragment, useState } from 'react';
import { useTranslate, Button, useRefresh } from 'react-admin';
import {
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  SvgIcon,
  CircularProgress,
} from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { useHandbook, useNotify } from '~/hooks';
import { EditButton, TextInput } from '~/components';
import { eventLogUpdate } from '~/utils';
import { forceKycUpdateBusiness, forceKycUpdateIndividual } from '~/api/kyc';
import { Business, Individual } from '~/types';
import { ShieldCheckmarkIcon } from '~/img';
import { useMutation } from 'react-query';
import MuiAlert from '@material-ui/lab/Alert';
import Typography from '@material-ui/core/Typography';

const label = (resource: string, key: string): string => `resources.${resource}.fields.${key}`;

interface Props {
  resource: string;
  record: Individual | Business | undefined;
  solutionId?: string;
}

const KYCLevelForceUpdate = (props: Props) => {
  const { solutionId, resource, record } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(label(resource, key));
  const notify = useNotify();

  const currentKYCLevel = record?.kycLevel;
  const [selectedKYCLevel, setSelectedKYCLevel] = useState<string>(currentKYCLevel || '');

  const forceRefresh = useRefresh();

  const [note, setNote] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const handleNoteInput = ({ target }: any) => {
    setErrorMessage(undefined);
    setNote(target.value);
  };

  const { mutateAsync: forceKycUpdateIndividualAsync } = useMutation(forceKycUpdateIndividual);
  const { mutateAsync: forceKycUpdateBusinessAsync } = useMutation(forceKycUpdateBusiness);

  const { data } = useHandbook(solutionId);
  const kycLevels = (data?.kycLevels || []).sort((a, b) => {
    return a.priority - b.priority;
  });

  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    if (currentKYCLevel) {
      setSelectedKYCLevel(currentKYCLevel);
    }
    setNote('');
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleCheckKyc = () => {
    if (!record || !selectedKYCLevel) return null;
    if (!note) {
      setErrorMessage(translate('ra.validation.required'));
      return null;
    }
    const mutateAsync =
      resource === 'individuals'
        ? forceKycUpdateIndividualAsync
        : resource === 'businesses'
        ? forceKycUpdateBusinessAsync
        : () => Promise.reject();

    setIsLoading(true);
    return mutateAsync({ profileId: record.id, body: { kycLevel: selectedKYCLevel, note } })
      .then(() => {
        eventLogUpdate(resource);
        forceRefresh();
        notify({ type: 'success', message: t('kycLevelUpdated') });
        handleClose();
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const classes = useStyles();

  return (
    <Fragment>
      <EditButton
        label={t('forceKYCLevelUpdate')}
        icon={
          <SvgIcon>
            <ShieldCheckmarkIcon />
          </SvgIcon>
        }
        onClick={handleOpen}
        red
      />
      <Dialog onClose={handleClose} aria-labelledby='simple-dialog-title' open={open}>
        <DialogTitle className={classes.DialogTitle} aria-label='simple-dialog-title'>
          {t('selectKYCtoCheck')}
        </DialogTitle>
        <DialogContent style={{ maxWidth: 450 }}>
          <Grid container justify='center'>
            {kycLevels?.map((item, index) => {
              return (
                <Grid item xs={12} key={item.id}>
                  <MenuItem
                    component='button'
                    selected={selectedKYCLevel === item.id}
                    onClick={() => setSelectedKYCLevel(item.id)}
                    key={index}
                    value={item.id}
                    className={classes.menuItem}
                    disabled={currentKYCLevel === item.id}
                  >
                    {item.name}
                  </MenuItem>
                </Grid>
              );
            })}
          </Grid>
          <Grid container spacing={1} justify='center' className={classes.topSpacing}>
            <Grid item xs={12}>
              <TextInput
                label={t('reason')}
                onChange={handleNoteInput}
                value={note}
                multiline
                rows={5}
                rowsMax={20}
                error={!!errorMessage}
                helperText={errorMessage}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={1}>
                <MuiAlert severity='warning'>
                  {/* extra Typography is needed because text style will change when an error occurs with an alert in snackbar */}
                  <Typography className={classes.alertText}>
                    {t('forceKYCLevelUpdateAlert')}
                  </Typography>
                </MuiAlert>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={1} justify='space-between' className={classes.topSpacing}>
            <Grid item>
              <Button label='ra.action.cancel' onClick={handleClose} />
            </Grid>
            <Grid item>
              <Button
                label={t('forceKYCLevelUpdate')}
                variant='contained'
                onClick={handleCheckKyc}
                disabled={selectedKYCLevel === currentKYCLevel || !selectedKYCLevel || isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={18} />
                ) : (
                  <SvgIcon>
                    <ShieldCheckmarkIcon />
                  </SvgIcon>
                )}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </Fragment>
  );
};

const useStyles = makeStyles((theme) => ({
  menuItem: {
    width: '100%',
    justifyContent: 'center',
  },
  topSpacing: {
    marginTop: theme.spacing(2),
  },
  DialogTitle: {
    '& > h2': {
      display: 'flex',
      justifyContent: 'center',
    },
  },
  alertText: {
    color: 'inherit',
  },
}));

export default KYCLevelForceUpdate;
